import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Flame, Sparkles, Rocket, MapPin, Globe, Clock, Bell, Gift, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import ProfileDetailModal from '../components/ProfileDetailModal';
import { useAuth } from '../contexts/AuthContext';
import { getDiscoverySections, getProfileCompletion, sendHeartbeat, getNotifications, startConversation } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { getPrimaryPhoto } from '../services/photoHelpers';

const SECTION_META = [
  { key: 'recommended', title: 'Recommended for you', icon: <Heart className="w-4 h-4 text-[#E2231A]" />, primary: true },
  { key: 'boosted', title: 'Boosted profiles', icon: <Rocket className="w-4 h-4 text-purple-500" /> },
  { key: 'online_now', title: 'Online now', icon: <span className="w-2 h-2 rounded-full bg-emerald-500" /> },
  { key: 'new_members', title: 'New members', icon: <Sparkles className="w-4 h-4 text-[#FFCE00]" /> },
  { key: 'popular', title: 'Popular profiles', icon: <Flame className="w-4 h-4 text-orange-500" /> },
  { key: 'guyana', title: 'Guyana connections', icon: <MapPin className="w-4 h-4 text-[#009E60]" /> },
  { key: 'international', title: 'International members', icon: <Globe className="w-4 h-4 text-blue-500" /> },
  { key: 'recently_active', title: 'Recently active', icon: <Clock className="w-4 h-4 text-gray-500" /> },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const defaultAvatar = useDefaultAvatar();
  const [sections, setSections] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [unread, setUnread] = useState(0);
  const [detailUserId, setDetailUserId] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const handleMessage = async (profile) => {
    if (!profile?.user_id) return;
    try {
      const res = await startConversation(profile.user_id);
      setDetailUserId(null);
      navigate(`/messages/${res.data.match_id}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402 || status === 403) {
        setDetailUserId(null);
        setUpgradePrompt({
          message: e.response.data?.detail ||
            'Premium members can message anyone. Free members must match first.'
        });
      } else {
        alert(e.response?.data?.detail || 'Failed to open conversation');
      }
    }
  };

  useEffect(() => {
    getDiscoverySections().then(r => setSections(r.data)).catch(() => {});
    getProfileCompletion().then(r => setCompletion(r.data)).catch(() => {});
    getNotifications(true, 1).then(r => setUnread(r.data.unread)).catch(() => {});
    sendHeartbeat().catch(() => {});
    const iv = setInterval(() => sendHeartbeat().catch(() => {}), 60000);
    return () => clearInterval(iv);
  }, []);

  const renderCard = (item, opts = {}) => {
    const isRec = opts.rec;
    const u = isRec ? item.user : item;
    if (!u?.user_id) return null;
    const photo = getPrimaryPhoto(u, defaultAvatar) || defaultAvatar;
    return (
      <button
        key={u.user_id}
        onClick={() => setDetailUserId(u.user_id)}
        className="flex-shrink-0 w-32 text-left group relative"
        data-testid={`home-card-${u.user_id}`}
      >
        <div className="relative w-32 h-40 rounded-2xl overflow-hidden bg-gray-100 shadow-sm group-hover:shadow-md transition-shadow">
          <img src={photo} alt={u.display_name || u.name} className="w-full h-full object-cover" loading="lazy" />
          {isRec && item.match_score >= 60 && (
            <span className="absolute top-2 left-2 bg-[#E2231A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {item.match_score}% match
            </span>
          )}
          {u.is_online && (
            <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
          )}
          {u.boost_until && new Date(u.boost_until) > new Date() && (
            <span className="absolute bottom-2 right-2 bg-purple-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Rocket className="w-2.5 h-2.5" /> Boosted
            </span>
          )}
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-1.5 left-2 right-2 text-white text-xs font-semibold truncate">
            {u.display_name || u.name}{u.age ? `, ${u.age}` : ''}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="home-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Hi, {user?.display_name || user?.name || 'there'} 👋
            </h1>
            <p className="text-xs text-gray-500">Discover people who fit you</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/boost')}
              className="p-2 rounded-full hover:bg-gray-100"
              title="Boost my profile"
              data-testid="home-boost-btn"
            >
              <Zap className="w-5 h-5 text-purple-500" />
            </button>
            <button
              onClick={() => navigate('/notifications')}
              className="p-2 rounded-full hover:bg-gray-100 relative"
              data-testid="home-notif-btn"
            >
              <Bell className="w-5 h-5 text-gray-700" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] px-1 flex items-center justify-center">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-4 space-y-5">
        {/* Profile completion nudge */}
        {completion && completion.percent < 90 && (
          <button
            onClick={() => navigate('/profile/setup')}
            className="w-full bg-gradient-to-r from-[#009E60] to-[#00b56d] text-white rounded-2xl p-4 text-left"
            data-testid="completion-banner"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">Your profile is {completion.percent}% complete</div>
                <div className="text-xs opacity-90">Fill in a bit more — get 3× more matches</div>
              </div>
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center font-bold">{completion.percent}%</div>
            </div>
            <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#FFCE00]" style={{ width: `${completion.percent}%` }} />
            </div>
          </button>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-4 gap-2">
          <QuickTile onClick={() => navigate('/discover')} icon={<Heart className="w-5 h-5" />} label="Swipe" color="bg-red-50 text-red-600" testId="quick-swipe" />
          <QuickTile onClick={() => navigate('/connect')} icon={<Sparkles className="w-5 h-5" />} label="Let's Connect" color="bg-yellow-50 text-yellow-700" testId="quick-connect" />
          <QuickTile onClick={() => navigate('/gifts/received')} icon={<Gift className="w-5 h-5" />} label="Gifts" color="bg-pink-50 text-pink-600" testId="quick-gifts" />
          <QuickTile onClick={() => navigate('/boost')} icon={<Rocket className="w-5 h-5" />} label="Boost" color="bg-purple-50 text-purple-600" testId="quick-boost" />
        </div>

        {/* Sections */}
        {SECTION_META.map(({ key, title, icon, primary }) => {
          const items = sections?.[key] || [];
          if (items.length === 0) return null;
          return (
            <section key={key} data-testid={`section-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {icon}
                  <h3 className={`font-semibold ${primary ? 'text-lg text-gray-900' : 'text-sm text-gray-800'}`} style={{ fontFamily: 'Outfit' }}>{title}</h3>
                </div>
                <button onClick={() => navigate('/search')} className="text-xs text-[#009E60] font-medium">See all</button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4">
                {items.map(item => renderCard(item, { rec: key === 'recommended' }))}
              </div>
            </section>
          );
        })}

        {sections === null && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent" />
          </div>
        )}
      </div>

      {detailUserId && (
        <ProfileDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onMessage={(p) => handleMessage(p)}
          onBlocked={() => setDetailUserId(null)}
        />
      )}

      <AnimatePresence>
        {upgradePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setUpgradePrompt(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-sm w-full text-center"
              data-testid="upgrade-prompt"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Message anyone with Premium
              </h3>
              <p className="text-gray-600 mb-5 text-sm">{upgradePrompt.message}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUpgradePrompt(null)} className="flex-1 rounded-full">Maybe later</Button>
                <Button
                  onClick={() => { setUpgradePrompt(null); navigate('/pricing'); }}
                  className="flex-1 bg-[#FFCE00] hover:bg-yellow-500 text-gray-900 rounded-full font-semibold"
                >
                  Go Premium
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

const QuickTile = ({ onClick, icon, label, color, testId }) => (
  <button onClick={onClick} className={`flex flex-col items-center gap-1 py-3 rounded-2xl ${color}`} data-testid={testId}>
    {icon}
    <span className="text-[11px] font-medium">{label}</span>
  </button>
);

export default HomePage;
